import { SCALE_MODES, Sprite, Texture } from "pixi.js"
import { PixelBuffer } from "./pixel_raster"
import { PX } from "./pixel_shipwreck_art"
import { MIRROR_AXIS, SHIP_ON_RIGHT } from "./shipwreck_geometry"

/**
 * A buffer of art pixels on screen: a texture uploaded straight from it, scaled up without smoothing.
 *
 * The login's shipwreck is authored with the ship on the left and mirrored onto the right, so its layers
 * mirror by default. A layer drawn the right way round already (the character builder's) passes
 * `mirrored: false`.
 */
export class PixelLayer {
    readonly sprite = new Sprite(Texture.EMPTY)
    private texture?: Texture
    private rgba?: Uint8Array
    private pixels?: Uint32Array
    private w = 0
    private h = 0

    constructor(private readonly mirrored: boolean = SHIP_ON_RIGHT) {}

    /** Size and place the layer for an authored art rectangle; the sprite lands wherever the mirror puts it. */
    place = (x0: number, y0: number, w: number, h: number) => {
        if (w !== this.w || h !== this.h || !this.texture) {
            this.texture?.destroy(true)
            this.w = w
            this.h = h
            this.rgba = new Uint8Array(w * h * 4)
            this.pixels = new Uint32Array(this.rgba.buffer)
            this.texture = Texture.fromBuffer(this.rgba, w, h, { scaleMode: SCALE_MODES.NEAREST })
            this.sprite.texture = this.texture
        }
        this.sprite.position.set(this.mirrored ? 2 * MIRROR_AXIS - (x0 + w) * PX : x0 * PX, y0 * PX)
        this.sprite.scale.set(PX)
    }

    /** Upload a buffer of this layer's size, mirrored onto the ship's side of the screen when the layer is. */
    commit = (buf: PixelBuffer) => {
        if (!this.pixels || !this.texture || buf.w !== this.w || buf.h !== this.h) {
            return
        }
        buf.writeRgba(this.pixels, this.mirrored)
        this.texture.baseTexture.update()
    }

    dispose = () => {
        this.sprite.destroy()
        this.texture?.destroy(true)
        this.texture = undefined
    }
}
